import React, { useState, useEffect } from "react";
import {
  Download,
  Search,
  RefreshCw,
  Users,
  FileSpreadsheet,
  Phone,
  User,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  X,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const CustomerDataTable = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // New state for month-year filter
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

  const { isLoggedIn, token } = useSelector((store) => store.userSlice);

  const fetchCustomers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiUrl}/user/allusers`, {
        headers: {
          token,
        },
      });
      if (response.data.success) {
        const fetchedData = response.data.data || [];
        console.log(fetchedData, "cus");
        setCustomers(fetchedData);
      } else {
        setError(response.data.message || "Failed to load customer data");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch customers",
      );
      console.error("Error fetching customers:", err);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Generate available months and years from customer data
  const getAvailableMonthsYears = () => {
    const monthsYears = new Set();
    customers.forEach((customer) => {
      if (customer.reg_date) {
        const date = new Date(customer.reg_date);
        const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        monthsYears.add(monthYear);
      }
    });
    return Array.from(monthsYears).sort().reverse();
  };

  const getDateRange = () => {
    const now = new Date();

    switch (dateFilter) {
      case "today": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0,
          0,
          0,
          0,
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999,
        );
        return { start, end };
      }

      case "yesterday": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 1,
          0,
          0,
          0,
          0,
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 1,
          23,
          59,
          59,
          999,
        );
        return { start, end };
      }

      case "last7days": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 7,
          0,
          0,
          0,
          0,
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999,
        );
        return { start, end };
      }

      case "last30days": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - 30,
          0,
          0,
          0,
          0,
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999,
        );
        return { start, end };
      }

      case "thisMonth": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth(),
          1,
          0,
          0,
          0,
          0,
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999,
        );
        return { start, end };
      }

      case "lastMonth": {
        const start = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
          0,
          0,
          0,
          0,
        );
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          0,
          23,
          59,
          59,
          999,
        );
        return { start, end };
      }

      case "thisYear": {
        const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        return { start, end };
      }

      case "lastYear": {
        const start = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
        const end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        return { start, end };
      }

      case "monthYear": {
        if (selectedMonth && selectedYear) {
          const month = parseInt(selectedMonth);
          const year = parseInt(selectedYear);
          const start = new Date(year, month, 1, 0, 0, 0, 0);
          const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
          return { start, end };
        }
        return null;
      }

      case "custom": {
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return null;
      }

      default:
        return null;
    }
  };

  const filterByDate = (customer) => {
    if (dateFilter === "all") return true;

    const dateRange = getDateRange();
    if (!dateRange) return true;

    // Parse the ISO date string - reg_date is the registration date
    const regDate = new Date(customer.reg_date);

    // Check if date is valid
    if (isNaN(regDate.getTime())) return false;

    // Convert all dates to UTC timestamps for accurate comparison
    const regTimestamp = regDate.getTime();
    const startTimestamp = dateRange.start.getTime();
    const endTimestamp = dateRange.end.getTime();

    return regTimestamp >= startTimestamp && regTimestamp <= endTimestamp;
  };

  const filteredCustomers = customers.filter((customer) => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = customer.name?.toLowerCase().includes(searchLower);
    const phoneMatch = customer.phone?.includes(searchTerm);
    const dobMatch = formatDate(customer.dob)
      .toLowerCase()
      .includes(searchLower);
    const moneyMatch = customer.funingo_money?.toString().includes(searchTerm);
    const dateMatch = filterByDate(customer);

    return (nameMatch || phoneMatch || dobMatch || moneyMatch) && dateMatch;
  });

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    dateFilter,
    customStartDate,
    customEndDate,
    selectedMonth,
    selectedYear,
  ]);

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const params = new URLSearchParams();

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      if (dateFilter !== "all") {
        params.append("dateFilter", dateFilter);

        if (dateFilter === "custom" && customStartDate && customEndDate) {
          params.append("startDate", customStartDate);
          params.append("endDate", customEndDate);
        }

        if (dateFilter === "monthYear" && selectedMonth && selectedYear) {
          params.append("month", selectedMonth);
          params.append("year", selectedYear);
        }
      }

      const response = await axios.get(
        `${apiUrl}/user/export-excel?${params.toString()}`,
        {
          responseType: "blob",
          headers: {
            token,
          },
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const filterSuffix =
        dateFilter !== "all" || searchTerm ? "_filtered" : "";
      link.setAttribute(
        "download",
        `FUNINGO_Customer_data${filterSuffix}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`,
      );

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        `${filteredCustomers.length} customer(s) downloaded successfully!`,
      );
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      toast.error("Failed to download Excel file. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value === "custom") {
      setShowCustomDatePicker(true);
      setShowMonthYearPicker(false);
    } else if (value === "monthYear") {
      setShowMonthYearPicker(true);
      setShowCustomDatePicker(false);
    } else {
      setShowCustomDatePicker(false);
      setShowMonthYearPicker(false);
      setCustomStartDate("");
      setCustomEndDate("");
      setSelectedMonth("");
      setSelectedYear("");
    }
  };

  const clearFilters = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setSelectedMonth("");
    setSelectedYear("");
    setShowCustomDatePicker(false);
    setShowMonthYearPicker(false);
    setSearchTerm("");
  };

  const hasActiveFilters = dateFilter !== "all" || searchTerm !== "";

  // Get month name from number
  const getMonthName = (monthNum) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[parseInt(monthNum)];
  };

  // Generate years (last 5 years + current
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear + 1; i >= currentYear - 5; i--) {
      years.push(i);
    }
    return years;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
      }}
    >
      {downloadingExcel && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "5px solid rgba(255, 255, 255, 0.3)",
              borderTop: "5px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <div
            style={{
              color: "white",
              fontSize: "20px",
              fontWeight: "600",
              marginTop: "20px",
            }}
          >
            Downloading Excel...
          </div>
          <div style={{ color: "rgba(255, 255, 255, 0.8)", marginTop: "10px" }}>
            Please wait while we prepare your file
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header Card */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "20px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Users size={28} color="white" />
              </div>
              <div>
                <h1
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#1f2937",
                    margin: 0,
                  }}
                >
                  Customer Database
                </h1>
                <p
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    margin: "4px 0 0 0",
                  }}
                >
                  {filteredCustomers.length} customers found
                </p>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button
                onClick={fetchCustomers}
                disabled={loading}
                style={{
                  padding: "12px 24px",
                  background: "white",
                  color: "#667eea",
                  border: "2px solid #667eea",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.3s ease",
                  opacity: loading ? 0.6 : 1,
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.background = "#667eea";
                    e.target.style.color = "white";
                    e.target.style.transform = "translateY(-2px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "white";
                  e.target.style.color = "#667eea";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                <RefreshCw size={18} />
                Refresh
              </button>

              <button
                onClick={downloadExcel}
                disabled={filteredCustomers.length === 0 || downloadingExcel}
                style={{
                  padding: "12px 24px",
                  background:
                    filteredCustomers.length === 0 || downloadingExcel
                      ? "#9ca3af"
                      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor:
                    filteredCustomers.length === 0 || downloadingExcel
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.3s ease",
                  boxShadow: downloadingExcel
                    ? "0 4px 12px rgba(107, 114, 128, 0.3)"
                    : "0 4px 12px rgba(16, 185, 129, 0.3)",
                }}
                onMouseEnter={(e) => {
                  if (filteredCustomers.length > 0 && !downloadingExcel) {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 6px 20px rgba(16, 185, 129, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = downloadingExcel
                    ? "0 4px 12px rgba(107, 114, 128, 0.3)"
                    : "0 4px 12px rgba(16, 185, 129, 0.3)";
                }}
              >
                <Download size={18} />
                {downloadingExcel
                  ? "Downloading..."
                  : `Download Excel (${filteredCustomers.length})`}
              </button>
            </div>
          </div>
        </div>

        {/* Date Filter Section */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "20px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Calendar size={20} color="#667eea" />
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1f2937",
                  margin: 0,
                }}
              >
                Filter by Registration Date
              </h3>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  padding: "6px 14px",
                  background: "#fee2e2",
                  color: "#dc2626",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#fecaca";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#fee2e2";
                }}
              >
                <X size={14} />
                Clear All
              </button>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "yesterday", label: "Yesterday" },
              { value: "last7days", label: "Last 7 Days" },
              { value: "last30days", label: "Last 30 Days" },
              { value: "thisMonth", label: "This Month" },
              { value: "lastMonth", label: "Last Month" },
              { value: "thisYear", label: "This Year" },
              { value: "lastYear", label: "Last Year" },
              { value: "monthYear", label: "Month & Year" },
              { value: "custom", label: "Custom Range" },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => handleDateFilterChange(filter.value)}
                style={{
                  padding: "8px 16px",
                  background:
                    dateFilter === filter.value
                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                      : "white",
                  color: dateFilter === filter.value ? "white" : "#1f2937",
                  border:
                    dateFilter === filter.value
                      ? "2px solid #667eea"
                      : "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (dateFilter !== filter.value) {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (dateFilter !== filter.value) {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.background = "white";
                  }
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Month & Year Picker */}
          {showMonthYearPicker && (
            <div
              style={{
                marginTop: "16px",
                padding: "20px",
                background: "#f9fafb",
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Select Month
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">-- Select Month --</option>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Select Year
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">-- Select Year --</option>
                    {generateYears().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedMonth !== "" && selectedYear && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    background: "white",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#667eea",
                  }}
                >
                  Filtering: {getMonthName(selectedMonth)} {selectedYear}
                </div>
              )}
            </div>
          )}

          {/* Custom Date Range Picker */}
          {showCustomDatePicker && (
            <div
              style={{
                marginTop: "16px",
                padding: "20px",
                background: "#f9fafb",
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "20px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={20}
              style={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9ca3af",
              }}
            />
            <input
              type="text"
              placeholder="Search by name, phone, date of birth or Funingo Money..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "14px 14px 14px 45px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                fontSize: "15px",
                outline: "none",
                transition: "all 0.3s ease",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#667eea";
                e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              background: "#fee2e2",
              border: "2px solid #fecaca",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              color: "#dc2626",
              fontWeight: "600",
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Table Card */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "5px solid #f3f4f6",
                  borderTop: "5px solid #667eea",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <div
                style={{
                  marginTop: "16px",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#6b7280",
                }}
              >
                Loading customers...
              </div>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
              }}
            >
              <FileSpreadsheet size={64} color="#d1d5db" />
              <div
                style={{
                  marginTop: "16px",
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#6b7280",
                }}
              >
                No customers found
              </div>
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "14px",
                  color: "#9ca3af",
                }}
              >
                {searchTerm || dateFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Click refresh to load data"}
              </div>
            </div>
          ) : (
            <>
              {/* Items per page selector */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    color: "#6b7280",
                  }}
                >
                  Show
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    style={{
                      padding: "8px 12px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      outline: "none",
                      background: "white",
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  entries
                </div>

                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredCustomers.length)} of{" "}
                  {filteredCustomers.length} entries
                </div>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      }}
                    >
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                          borderTopLeftRadius: "12px",
                        }}
                      >
                        S.No
                      </th>
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Phone size={16} />
                          Phone Number
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <User size={16} />
                          Customer Name
                        </div>
                      </th>
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        Funingo Coins
                      </th>
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        Date of Birth
                      </th>
                      <th
                        style={{
                          padding: "16px",
                          textAlign: "left",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                          borderTopRightRadius: "12px",
                        }}
                      >
                        Registration Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentCustomers.map((customer, index) => (
                      <tr
                        key={customer.phone || index}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          transition: "all 0.2s ease",
                          background: "white",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f9fafb";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "white";
                        }}
                      >
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "14px",
                            color: "#6b7280",
                            fontWeight: "500",
                          }}
                        >
                          {startIndex + index + 1}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "14px",
                            color: "#1f2937",
                            fontWeight: "500",
                          }}
                        >
                          {customer.phone || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "14px",
                            color: "#1f2937",
                            fontWeight: "600",
                          }}
                        >
                          {customer.name || "N/A"}
                        </td>
                        <td style={{ padding: "16px" }}>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "6px 12px",
                              background:
                                "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                              borderRadius: "20px",
                              position: "relative",
                            }}
                          >
                            {/* Outer coin circle with gradient */}
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                background:
                                  "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 8px rgba(245, 158, 11, 0.4)",
                                position: "relative",
                              }}
                            >
                              {/* Inner circle */}
                              <div
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  background:
                                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  color: "white",
                                }}
                              >
                                ₹
                              </div>
                              {/* Coin shine effect */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: "2px",
                                  left: "3px",
                                  width: "8px",
                                  height: "8px",
                                  background: "rgba(255, 255, 255, 0.5)",
                                  borderRadius: "50%",
                                  filter: "blur(1px)",
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "white",
                              }}
                            >
                              {customer.funingo_money || 0}
                            </span>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "14px",
                            color: "#6b7280",
                          }}
                        >
                          {formatDate(customer.dob)}
                        </td>
                        <td
                          style={{
                            padding: "16px",
                            fontSize: "14px",
                            color: "#6b7280",
                          }}
                        >
                          {formatDate(customer.reg_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                style={{
                  marginTop: "24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: "500",
                  }}
                >
                  Page {currentPage} of {totalPages}
                </div>

                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 12px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: currentPage === 1 ? "#9ca3af" : "#1f2937",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "#667eea";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "white";
                        e.target.style.color = "#1f2937";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    First
                  </button>

                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 12px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "#667eea";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <ChevronLeft
                      size={18}
                      color={currentPage === 1 ? "#9ca3af" : "#1f2937"}
                    />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        style={{
                          padding: "8px 14px",
                          background:
                            currentPage === pageNum
                              ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                              : "white",
                          border:
                            currentPage === pageNum
                              ? "2px solid #667eea"
                              : "2px solid #e5e7eb",
                          color: currentPage === pageNum ? "white" : "#1f2937",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          minWidth: "40px",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.background = "#f3f4f6";
                            e.target.style.borderColor = "#667eea";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (currentPage !== pageNum) {
                            e.target.style.background = "white";
                            e.target.style.borderColor = "#e5e7eb";
                          }
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 12px",
                      background:
                        currentPage === totalPages ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "#667eea";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "white";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    <ChevronRight
                      size={18}
                      color={currentPage === totalPages ? "#9ca3af" : "#1f2937"}
                    />
                  </button>

                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 12px",
                      background:
                        currentPage === totalPages ? "#f3f4f6" : "white",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: currentPage === totalPages ? "#9ca3af" : "#1f2937",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "#667eea";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "#667eea";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.background = "white";
                        e.target.style.color = "#1f2937";
                        e.target.style.borderColor = "#e5e7eb";
                      }
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDataTable;
